import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';

import { NativeMarket } from '@capgo/capacitor-native-market';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { App } from '@capacitor/app';
import { GamesService } from './games.service';

interface AppSettings {
    current: string;
    enabled: boolean;
    pixelEnabled: boolean;
    onlinePolling: number;
    msg?: {
        title: string;
        msg: string;
        btn: string;
    };
    majorMsg?: {
        title: string;
        msg: string;
        btn: string;
    };
    minorMsg?: {
        title: string;
        msg: string;
        btn: string;
    };
}

@Injectable()
export class SettingsService {

    private interval;
    private alert;

    public pixelEnabled: boolean = false;
    private pixelEnabledInitialized = false;

    public onlinePolling: number = 2;

    /**
     * Constructor
     */
    constructor(
        private http: HttpClient,
        private iab: InAppBrowser,
        private platform: Platform,
        private alertCtrl: AlertController,
        private gamesService: GamesService
    ) {}

    /**
     * Startup: check app state
     */
    public async startup() {
        this.startUpdate();
    }

    /**
     * Start settings update
     */
    public async startUpdate() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
        this.interval = setInterval( _ => {
            this.update();
        }, 1000 * 60 * 2);

        this.update();
    }   

    /**
     * Update settings
     */
    public update() {
        this.http.get(environment.apiUrl + '/appversion').subscribe(async (info:AppSettings) => {
            if (!info.enabled) {
                this.presentAlert(info.msg.title, info.msg.msg);
            } else {
                this.dismissAlert();
                const serverVersion = info.current.replace(/\s*\(.*?\)\s*/g, '').split('.');

                if (this.platform.is('hybrid')) {
                    const infos = await App.getInfo();
                    const splittedVersion = infos.version.replace(/\s*\(.*?\)\s*/g, '').split('.');
                    
                    if (Number(serverVersion[0]) > Number(splittedVersion[0])) {
                        this.presentAlert(info.majorMsg.title, info.majorMsg.msg, info.majorMsg.btn);
                    } else if (Number(serverVersion[0]) == Number(splittedVersion[0]) && Number(serverVersion[1]) > Number(splittedVersion[1])) {
                        this.presentAlert(info.majorMsg.title, info.majorMsg.msg, info.majorMsg.btn);
                    } else if (Number(serverVersion[0]) == Number(splittedVersion[0]) && Number(serverVersion[1]) == Number(splittedVersion[1]) && Number(serverVersion[2]) > Number(splittedVersion[2])) {
                        this.presentAlert(info.minorMsg.title, info.minorMsg.msg, info.minorMsg.btn, true);
                    }
                }

                this.onlinePolling = info.onlinePolling;

                if (!this.pixelEnabledInitialized || info.pixelEnabled !== this.pixelEnabled) {
                    this.pixelEnabled = info.pixelEnabled;
                    this.pixelEnabledInitialized = true;
                    if (info.pixelEnabled) {
                        this.gamesService.startup();
                    } else {
                        this.gamesService.destroy();
                    }
                }
            }
        });
    }

    /**
     * Open App store
     */
    openAppStore() {
        if (this.platform.is('android')) {
            NativeMarket.openStoreListing({
                appId: 'com.begaym.app'
            });
        } else {
            this.iab.create('itms-apps://itunes.apple.com/app/id1623816795');
        }
    }

    /**
     * Present alert
     */
    async presentAlert(header, message, buttonText = '', allowClose = false) {
        if (!this.alert) {

            const buttons:any = [];

            if (buttonText != '') {
                buttons.push({
                    text: buttonText,
                    handler: () => {
                        this.openAppStore();
                        this.alert = null;
                        return false;
                    }
                });
            }

            if (allowClose) {
                buttons.push({
                    text: 'Fermer',
                    role: 'cancel',
                    handler: () => {
                        this.alert = null;
                    }
                });
            }

            this.alert = await this.alertCtrl.create({
                header,
                message,
                buttons,
                backdropDismiss: allowClose
            });
    
            await this.alert.present();
        }
    }

    /**
     * Present alert
     */
    async dismissAlert() {
        if (this.alert) {
            this.alertCtrl.dismiss({
                'dismissed': true
            });

            this.alert = null;
        }
    }
}
